import { type CertificationPeriodViewModel, FrequencyStatusEnum, type SearchFrequencyViewModel } from '@generated';
import { useGetCertificationPeriodSchedule } from '~/features/scheduling/queries';
import { CertificationPeriodCalendar } from '~/features/scheduling/cert-periods/CertificationPeriodCalendar';
import { convertFrequenciesToCalendarEvent } from '~/features/frequencies/util';
import { Section } from '~/shared/section/Section';
import { SectionTitle } from '~/shared/section/SectionTitle';
import { SectionActionButton } from '~/shared/components/buttons/SectionActionButton';
import { Calendar, CalendarSync, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { Dialog } from 'react-aria-components';
import { VisitModal } from '~/features/visits/VisitModal';
import { PendingMissedVisitModal } from '~/features/frequencies/missed/PendingMissedVisitModal';
import { Popover } from '~/shared/components/Popover';
import { Button } from '~/shared/components/buttons/Button';
import { RescheduleFrequenciesFormModal } from '~/features/frequencies/reschedule/RescheduleFrequenciesFormModal';

type SelectedFrequency = { frequency: SearchFrequencyViewModel; target: Element };

export function CertificationPeriodsSchedule({ certificationPeriod }: { certificationPeriod: CertificationPeriodViewModel }) {
	const { data: existingFrequencies } = useGetCertificationPeriodSchedule({
		certificationPeriod: certificationPeriod
	});
	const [viewFrequency, setViewFrequency] = useState<SearchFrequencyViewModel | null>(null);
	const [pendingMissed, setPendingMissed] = useState<SelectedFrequency | null>(null);
	const [completeMissedFrequency, setCompleteMissedFrequency] = useState<SearchFrequencyViewModel | null>(null);
	const [reschedule, setReschedule] = useState<{ frequency?: SearchFrequencyViewModel } | null>(null);

	const frequencyEvents = convertFrequenciesToCalendarEvent(existingFrequencies);

	const handleCalendarItemClicked = (frequencyId: string, target: Element) => {
		const frequency = existingFrequencies.find(x => x.id === frequencyId)!;
		if (frequency.frequencyStatusId === FrequencyStatusEnum.PendingMissedVisit) {
			setPendingMissed({ frequency, target });
			return;
		}
		setViewFrequency(frequency);
	};

	return (
		<Section
			header={
				<SectionTitle
					title="Certification Period Schedule"
					actions={
						<div className="flex items-center gap-2">
							<SectionActionButton Icon={CalendarSync} aria-label="Reschedule" onClick={() => setReschedule({})} />
						</div>
					}
				/>
			}
		>
			<CertificationPeriodCalendar
				events={frequencyEvents}
				certificationPeriod={certificationPeriod}
				onCalendarItemClicked={(frequency, target) => handleCalendarItemClicked(frequency.id, target)}
			/>
			{pendingMissed ? (
				<Popover
					triggerRef={{ current: pendingMissed.target }}
					isOpen
					onOpenChange={isOpen => !isOpen && setPendingMissed(null)}
					showArrow
					className="p-2"
				>
					<Dialog aria-label="Pending missed visit actions" className="flex flex-col gap-1 outline-none">
						<Button
							Icon={Calendar}
							label="Reschedule"
							appearance="ghost"
							intent="neutral"
							className="w-full justify-start"
							onClick={() => {
								setReschedule({ frequency: pendingMissed.frequency });
								setPendingMissed(null);
							}}
						/>
						<Button
							Icon={CheckCircle}
							label="Complete Missed Visit"
							appearance="ghost"
							intent="neutral"
							className="w-full justify-start"
							onClick={() => {
								setCompleteMissedFrequency(pendingMissed.frequency);
								setPendingMissed(null);
							}}
						/>
					</Dialog>
				</Popover>
			) : null}
			{viewFrequency?.visitId ? (
				<VisitModal stayId={viewFrequency.stayId} visitId={viewFrequency.visitId} onClose={() => setViewFrequency(null)} />
			) : null}
			{reschedule ? (
				<RescheduleFrequenciesFormModal certificationPeriod={certificationPeriod} frequency={reschedule.frequency} closeModal={() => setReschedule(null)} />
			) : null}
			{completeMissedFrequency ? (
				<PendingMissedVisitModal
					stayId={completeMissedFrequency.stayId}
					frequencyId={completeMissedFrequency.id}
					onClose={() => setCompleteMissedFrequency(null)}
				/>
			) : null}
		</Section>
	);
}
